import { sha256 } from 'js-sha256';

function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}

var ANALYTICS_NOT_INIALIZED_TEST_USER = '[CFC Analytics]: NOT INITIALIZED, user is a test automation user.';
var CFC_USER_DOMAIN = '@cfchildren.org';
var CFC_USER_PREFIX = 'CFC-USER';
var CONFIG_NOT_VALID_OBJECT = '[CFC Analytics]: The UserAnalytics config object is not an OBJECT. Please refer to the UserConfig typing within the @cfc/analytics library for more details.';
var EMAIL_REQUIRED = '[CFC Analytics]: Email is required to initialize UserAnalytics. Please make sure email is of type STRING.';
var LOCALHOST = 'LOCALHOST';
var LOCALHOST_ENV = 'localhost';
var NONPROD_ENV = '-np';
var NO_PENDO_INCLUDED = '[CFC Analytics]: Pendo is not available on the window object. Please make sure to include the Pendo script in your application.';
var ORIGINATION_DATE_NOT_VALID = '[CFC Analytics]: WARNING, The UserAnalytics visitor.origination_date key is not a valid DATE. Please make sure visitor.origination_date is a valid date.';
var PROD = 'PROD';
var TEST_USER_DOMAIN = '@cfctest.org';
var USER_ANALYTICS_INITIALIZED = '[CFC Analytics] User Analytics client initialized';
var USER_ID_REQUIRED = '[CFC Analytics]: User ID at a minumum is required to initialize UserAnalytics. Please make sure userId is of type STRING or NUMBER.';

/**
 * @description Creates a prefix for the account based on the current environment. Also includes the string 'site'
 * @param undefined
 * @returns {string}
 */
function createAccountIdPrefix() {
  var envPrefix = getCurrentEnvironment();
  if (envPrefix === PROD) {
    return 'site';
  } else {
    return envPrefix + "-np-site";
  }
}
/**
 * @description Creates a prefix for the user based on the current environment and will also prefix CFC-USER if user is a cfc user
 * @param email
 * @returns {string}
 */
function createUserIdPrefix(email) {
  var envPrefix = getCurrentEnvironment();
  if (email && email.includes(CFC_USER_DOMAIN)) {
    return envPrefix + "-" + CFC_USER_PREFIX;
  }
  return "" + envPrefix;
}
/**
 * @description Formats the date string to ISO 8601 format with milliseconds and adds 'Z' to indicate UTC
 * @param dateString {string}
 * @returns {string}
 */
function formatToISO8601W3CWithMilliseconds(dateString) {
  // Regular expression to match the basic ISO 8601 format
  // This function is needed because the current source of data for this date field, SS API is returning dates with inconsistent formats
  // like '2023-11-07T19:02:58.84' or '2018-10-25T14:12:00'
  // This function ensures the formats are make consistent prior to sending to third party analytics
  var iso8601BasicRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;
  var iso8601WithMSRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+$/;
  var iso8601WithMSAndZRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+Z$/;
  // Check if the input string matches the basic ISO 8601 format
  if (iso8601BasicRegex.test(dateString)) {
    // Append milliseconds and 'Z' to indicate UTC
    return dateString + '.000Z';
  } // Check if the input string matches the ISO 8601 format with milliseconds but without Z
  else if (iso8601WithMSRegex.test(dateString)) {
    return dateString + 'Z';
  }
  // Check if the input string matches the ISO 8601 format with milliseconds and Z
  else if (iso8601WithMSAndZRegex.test(dateString)) {
    return dateString;
  } else {
    console.warn(ORIGINATION_DATE_NOT_VALID);
    // this case should never happen because SSAPI should always return a valid ISO 8601 string
    return dateString;
  }
}
/**
 * @description Finds the current environment and returns an uppercased string
 * @returns {string} - The current environment
 */
function getCurrentEnvironment() {
  // TODO: Eventually see if we can pull in the environment from a variable. Currently the way that our static apps work is they read from the process.env
  // variables at build time and set all of their variables need later for run time. Since this code is only run at run time in a static file the process.env variables are not available
  var isLocalhost = window.location.host.includes(LOCALHOST_ENV);
  var host = window.location.host.split('.')[0];
  if (isLocalhost) {
    return LOCALHOST;
  } else if (host.includes(NONPROD_ENV)) {
    return host.split('-')[2].toUpperCase();
  } else {
    return PROD;
  }
}
/**
 * @description Parses licenses on a sitre and returns a list of programs that are associated with the site
 * @params sites of type Site
 * @returns {string} - A string of programs
 */
function getUniqueProgramCodesFromLicenses(licenses) {
  if (!licenses || licenses.length === 0) {
    return [];
  }
  var programCodes = licenses.flatMap(function (license) {
    return license.product.programCodes;
  });
  var uniqueProgramCodes = Array.from(new Set(programCodes));
  return uniqueProgramCodes;
}
/**
 * @description Goes over a collection of sites and returns the site with the lowest id and therefore oldest site
 * @param sites of type Site[]
 * @returns Site | undefined
 */
function getSiteByOldestId(sites) {
  if (!sites || sites.length === 0) {
    return null;
  }
  return sites.reduce(function (prevSmallestSite, currentSite) {
    if ((prevSmallestSite == null ? void 0 : prevSmallestSite.id) === undefined) {
      return currentSite;
    }
    return currentSite.id < prevSmallestSite.id ? currentSite : prevSmallestSite;
  }, null);
}
/**
 * @description Takes in a user email string and determines if the user is a CFC user or not
 * @param email {string}
 * @returns {boolean}
 */
function isUserEmailCfcAndProdEnv(email) {
  if (!email) {
    return false;
  }
  var envPrefix = getCurrentEnvironment();
  var isCFCUserEmail = email.includes(CFC_USER_DOMAIN);
  var isCFCUser = isCFCUserEmail && envPrefix === PROD;
  return isCFCUser;
}
/**
 * @description Creates a unique sha 256 hashed ID for the current user to obsfuscate the user's identity and prefixes current environment
 * @param {string} userId - The user's ID
 * @returns {string} - The hashed user ID
 */
function setHashedUserId(userId, userPrefix) {
  return userPrefix + "-" + sha256(userId);
}

var UserAnalytics = /*#__PURE__*/function () {
  function UserAnalytics() {}
  var _proto = UserAnalytics.prototype;
  /**
   * @description Initializes the Pendo library with the user's information sent in from the client
   * @param userConfig {UserConfig}
   * @returns void
   */
  _proto.initialize = function initialize(userConfig) {
    if (!window.pendo) {
      console.warn(NO_PENDO_INCLUDED);
      return;
    }
    var _this$validateUserCon = this.validateUserConfig(userConfig),
      isValidUserConfig = _this$validateUserCon.isValidUserConfig,
      errorMessage = _this$validateUserCon.errorMessage;
    if (!isValidUserConfig) {
      console.warn(errorMessage);
      return;
    }
    if (userConfig.email && userConfig.email.includes(TEST_USER_DOMAIN)) {
      console.warn(ANALYTICS_NOT_INIALIZED_TEST_USER);
      return;
    }
    if (!UserAnalytics.isPendoInitalized) {
      var pendoConfig = this.createPendoConfig(userConfig);
      window.pendo.initialize(pendoConfig);
      console.info(USER_ANALYTICS_INITIALIZED);
      UserAnalytics.isPendoInitalized = true;
    }
  }
  /**
   * @description Creates the account payload object to be sent to Pendo
   * @param config of type UserConfig
   * @returns {accountPayload: Partial<PendoConfig['account']>, hasAccountInfo: boolean}
   */;
  _proto.createAccountPayload = function createAccountPayload(config) {
    var accountPayload = {
      id: '',
      site_name: '',
      purchaser: '',
      purchaser_id: '',
      geographic_state: '',
      geographic_state_code: '',
      geographic_country: '',
      origination_date: '',
      programs: []
    };
    var hasAccountInfo = false;
    if (config.sites && Array.isArray(config.sites) && config.sites.length > 0) {
      var _selectedSite$organiz;
      var selectedSite = getSiteByOldestId(config.sites);
      var getAccountIdPrefix = createAccountIdPrefix();
      var hasRequiredAccountInfo = selectedSite && (selectedSite == null ? void 0 : selectedSite.id) && ((_selectedSite$organiz = selectedSite.organization) == null ? void 0 : _selectedSite$organiz.name);
      if (hasRequiredAccountInfo) {
        var _selectedSite$address, _selectedSite$address2, _selectedSite$address3;
        hasAccountInfo = true;
        accountPayload.id = getAccountIdPrefix + "-" + selectedSite.id.toString();
        accountPayload.site_name = selectedSite.name || '';
        accountPayload.purchaser = selectedSite.organization.name;
        accountPayload.purchaser_id = selectedSite.organization.netSuiteCustomerId.toString();
        accountPayload.geographic_country = ((_selectedSite$address = selectedSite.address) == null ? void 0 : _selectedSite$address.country) || '';
        accountPayload.geographic_state = ((_selectedSite$address2 = selectedSite.address) == null ? void 0 : _selectedSite$address2.region) || '';
        accountPayload.geographic_state_code = ((_selectedSite$address3 = selectedSite.address) == null ? void 0 : _selectedSite$address3.regionCode) || '';
        accountPayload.origination_date = formatToISO8601W3CWithMilliseconds(selectedSite.createdDate) || '';
        accountPayload.programs = getUniqueProgramCodesFromLicenses(selectedSite.licenses);
      }
    }
    return {
      accountPayload: accountPayload,
      hasAccountInfo: hasAccountInfo
    };
  }
  /**
   * @description Curates the config object to be passed into the Pendo initialize function
   * @param config of type UserConfig
   * @returns {PendoConfig}
   */;
  _proto.createPendoConfig = function createPendoConfig(config) {
    var curatedPendoConfig;
    var _this$createAccountPa = this.createAccountPayload(config),
      accountPayload = _this$createAccountPa.accountPayload,
      hasAccountInfo = _this$createAccountPa.hasAccountInfo;
    if (hasAccountInfo) {
      curatedPendoConfig = {
        visitor: _extends({}, this.createVisitorPayload(config)),
        account: _extends({}, accountPayload)
      };
    } else {
      // TODO we need to figure out how to let our larger system know some users do not have account info
      curatedPendoConfig = {
        visitor: _extends({}, this.createVisitorPayload(config))
      };
    }
    if (config.hasIframe) {
      curatedPendoConfig.autoFrameInstall = true;
      curatedPendoConfig.frameIdentitySync = true;
    }
    return curatedPendoConfig;
  }
  /**
   * @description Creates the visitor payload object to be sent to Pendo
   * @param config of type UserConfig
   * @returns {Partial<PendoConfig['visitor']>}
   */;
  _proto.createVisitorPayload = function createVisitorPayload(config) {
    var userEmail = config.email || '';
    var visitor = {
      id: setHashedUserId(config.userId.toString(), createUserIdPrefix(userEmail)),
      post_migration_user_login: true
    };
    // This populates the visitor object with the profile information if it exists
    if (config.profile && typeof config.profile == 'object') {
      var _config$profile;
      var hasArchetype = ((_config$profile = config.profile) == null ? void 0 : _config$profile.archetypes) && Array.isArray(config.profile.archetypes) && config.profile.archetypes.length > 0;
      if (hasArchetype) {
        var _config$profile$arche;
        (_config$profile$arche = config.profile.archetypes) == null || _config$profile$arche.forEach(function (archetype) {
          if (typeof archetype == 'object' && archetype.archetypeName) {
            visitor.archetypes = visitor.archetypes || [];
            visitor.archetypes.push(archetype.archetypeName);
          }
        });
      }
      var hasCreatedDate = config.profile.createdDate;
      if (hasCreatedDate) {
        visitor.user_account_creation_date = formatToISO8601W3CWithMilliseconds(config.profile.createdDate);
      }
      var hasUserAssignedRole = config.profile.title && typeof config.profile.title === 'string';
      if (hasUserAssignedRole) {
        visitor.job_title = config.profile.title || '';
      }
      var hasUserRoles = config.profile.roles && Array.isArray(config.profile.roles);
      if (hasUserRoles) {
        visitor.user_site_roles = config.profile.roles || [];
      }
      var isCfcUserAndProdEnv = isUserEmailCfcAndProdEnv(userEmail);
      if (isCfcUserAndProdEnv) {
        visitor.email = userEmail;
      }
    }
    // This populates the visitor object with the site information if it exists
    if (config.sites && Array.isArray(config.sites) && config.sites.length > 0) {
      var isSingleSite = config.sites.length === 1;
      visitor.single_site = isSingleSite;
      // num_admin_sites = loop over sites array and return a count of the sites where claims array contains "Admin" string
      var numAdminSites = config.sites.filter(function (site) {
        return site.claims.includes('Admin');
      }).length;
      visitor.num_admin_sites = numAdminSites;
    }
    return visitor;
  }
  /**
   * @description Validates that the the config object passed in by the consumer for required fields
   */;
  _proto.validateUserConfig = function validateUserConfig(config) {
    var response = {
      errorMessage: '',
      isValidUserConfig: false
    };
    //Validate that config is an object and it at least has a userId key and email
    if (!config || typeof config !== 'object') {
      response.errorMessage = CONFIG_NOT_VALID_OBJECT;
    } else if (!config.userId && (typeof config.userId !== 'string' || typeof config.userId !== 'number')) {
      response.errorMessage = USER_ID_REQUIRED;
    } else if (!config.email || typeof config.email !== 'string') {
      response.errorMessage = EMAIL_REQUIRED;
    } else {
      response.isValidUserConfig = true;
    }
    return response;
  };
  return UserAnalytics;
}();
UserAnalytics.isPendoInitalized = false;
var UserAnalyticsSingleton = /*#__PURE__*/new UserAnalytics();

export { UserAnalyticsSingleton as UserAnalytics };
//# sourceMappingURL=analytics.esm.js.map
